/**
 * Cleanroom authentication — development only.
 *
 * The cleanroom seeds one user per workspace role, but those users exist in the
 * cleanroom database and have no Google account, so the normal login cannot
 * reach them. The backend already issues locally-signed HS256 tokens for them
 * via `POST /system/token?sub=...`, and accepts those tokens only while
 * `CLEANROOM_MODE=true`. This module is the client for that endpoint.
 *
 * Everything here is reached only when REACT_APP_USE_CLEANROOM_AUTH === 'true'.
 * react-scripts inlines that at build time, so a production bundle drops this
 * module along with the branch that imports it.
 *
 * Against production the endpoint returns 403 (`require_cleanroom`), so a build
 * shipped with the flag on simply cannot sign in.
 */

const API = process.env.REACT_APP_BACKEND_URL;

export const CLEANROOM_AUTH_ENABLED =
  process.env.REACT_APP_USE_CLEANROOM_AUTH === 'true';

const TOKEN_KEY = 'cleanroom_token';
const SUB_KEY = 'cleanroom_sub';

/**
 * Identities the seeders create. `cleanroom|*` comes from POST /system/bootstrap
 * (the integration fixture); `demo|*` from `make db-seed-demo`. Both are listed
 * because either dataset may be present, and probing tells us which actually is.
 */
export const SEEDED_IDENTITIES = [
  { sub: 'cleanroom|test', username: 'cleanroom', role: 'Owner', dataset: 'cleanroom' },
  { sub: 'cleanroom|coowner', username: 'cleanroom-coowner', role: 'Co-Owner', dataset: 'cleanroom' },
  { sub: 'cleanroom|member', username: 'cleanroom-member', role: 'Member', dataset: 'cleanroom' },
  { sub: 'cleanroom|advisor', username: 'cleanroom-advisor', role: 'Advisor', dataset: 'cleanroom' },
  { sub: 'cleanroom|viewer', username: 'cleanroom-viewer', role: 'Viewer', dataset: 'cleanroom' },
  { sub: 'demo|test', username: 'demo', role: 'Owner', dataset: 'demo' },
  { sub: 'demo|coowner', username: 'demo-coowner', role: 'Co-Owner', dataset: 'demo' },
  { sub: 'demo|member', username: 'demo-member', role: 'Member', dataset: 'demo' },
  { sub: 'demo|advisor', username: 'demo-advisor', role: 'Advisor', dataset: 'demo' },
  { sub: 'demo|viewer', username: 'demo-viewer', role: 'Viewer', dataset: 'demo' },
];

/** Mint a token for one seeded identity. Throws with a readable reason. */
export async function fetchCleanroomToken(sub) {
  const resp = await fetch(
    `${API}/system/token?sub=${encodeURIComponent(sub)}`,
    { method: 'POST' }
  );

  if (resp.status === 403) {
    throw new Error(
      'The backend is not in cleanroom mode. Start it with CLEANROOM_MODE=true ' +
        '(cd mi-kapital-backend && make cleanrun).'
    );
  }
  if (resp.status === 404) {
    throw new Error(
      `No seeded user for "${sub}". Run POST /system/bootstrap, or ` +
        '`make db-seed-demo` for the demo dataset.'
    );
  }
  if (!resp.ok) {
    throw new Error(`/system/token returned ${resp.status}`);
  }

  const { access_token: token } = await resp.json();
  return token;
}

/**
 * Which seeded identities actually exist right now.
 *
 * Probed rather than assumed, so the picker shows only what you can sign in as
 * — the cleanroom and demo datasets are separate and either may be absent.
 * Minting a token has no side effects, so probing is safe.
 */
export async function discoverIdentities() {
  // Probe each dataset's Owner first and only expand the rest if it exists.
  // Probing a dataset that was never seeded logs one failed request instead of
  // five, which keeps the console readable when only one dataset is present.
  const datasets = ['cleanroom', 'demo'];
  const present = await Promise.all(
    datasets.map(async (dataset) => {
      const owner = SEEDED_IDENTITIES.find(
        (i) => i.dataset === dataset && i.role === 'Owner'
      );
      try {
        await fetchCleanroomToken(owner.sub);
        return dataset;
      } catch {
        return null;
      }
    })
  );

  const live = present.filter(Boolean);
  const candidates = SEEDED_IDENTITIES.filter((i) => live.includes(i.dataset));

  const results = await Promise.all(
    candidates.map(async (identity) => {
      if (identity.role === 'Owner') return identity; // already probed
      try {
        await fetchCleanroomToken(identity.sub);
        return identity;
      } catch {
        return null;
      }
    })
  );
  return results.filter(Boolean);
}

export function storeSession(token, sub) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(SUB_KEY, sub);
  // The active workspace belongs to the previous user. Carrying it across a
  // switch produces 403s that look like application bugs.
  localStorage.removeItem('mikapital_active_workspace_id');
}

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredSub() {
  return localStorage.getItem(SUB_KEY);
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SUB_KEY);
  localStorage.removeItem('mikapital_active_workspace_id');
}
